import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { adminLogin, isAdminAuthenticated } from '../api/auth';
import { useNavigate } from 'react-router-dom';
const Diamond = ({ size = 6, color = '#B08D57', style = {} }) => (
  <span
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      background: color,
      transform: 'rotate(45deg)',
      flexShrink: 0,
      ...style,
    }}
  />
);

/* ══════════════════════════════════════════════════════════════
   ADMIN LOGIN
══════════════════════════════════════════════════════════════ */
const AdminLogin = ({ onAuthSuccess }) => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [shake, setShake]       = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const emailRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Admin Login | Aamira';
    if (isAdminAuthenticated()) navigate('/admin');
    emailRef.current?.focus();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');

    try {
      
      await adminLogin({ email, password });
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.message || 'Authentication failed.');
      setShake(true);
      setTimeout(() => setShake(false), 600);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .aln-root {
          min-height: 100vh;
          display: flex;
          font-family: 'Jost', sans-serif;
          background: #0F0E0C;
          overflow: hidden;
          position: relative;
        }

        /* ── Left decorative panel ── */
        .aln-panel {
          flex: 1;
          display: none;
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 900px) {
          .aln-panel { display: flex; flex-direction: column; justify-content: center; align-items: center; }
        }

        .aln-panel-bg {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 80% 60% at 30% 40%, rgba(176,141,87,0.18) 0%, transparent 60%),
            radial-gradient(ellipse 50% 70% at 70% 80%, rgba(176,141,87,0.08) 0%, transparent 55%),
            #100F0D;
        }

        /* Geometric grid lines */
        .aln-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(176,141,87,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(176,141,87,0.04) 1px, transparent 1px);
          background-size: 48px 48px;
        }

        /* Corner ornaments */
        .aln-corner {
          position: absolute;
          width: 80px;
          height: 80px;
          border-color: rgba(176,141,87,0.3);
          border-style: solid;
        }
        .aln-corner--tl { top: 32px; left: 32px; border-width: 1px 0 0 1px; }
        .aln-corner--br { bottom: 32px; right: 32px; border-width: 0 1px 1px 0; }

        .aln-panel-content {
          position: relative;
          z-index: 2;
          text-align: center;
          padding: 48px;
        }
        .aln-panel-wordmark {
          font-family: 'Playfair Display', serif;
          font-size: clamp(38px, 4vw, 52px);
          font-weight: 700;
          color: #D4AF78;
          letter-spacing: 0.04em;
          line-height: 1;
          margin-bottom: 6px;
        }
        .aln-panel-wordmark em {
          display: block;
          font-weight: 400;
          font-style: italic;
          font-size: 0.52em;
          color: rgba(212,175,120,0.5);
          letter-spacing: 0.22em;
          text-transform: uppercase;
          margin-top: 6px;
        }
        .aln-panel-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: center;
          margin: 28px auto;
          width: 200px;
        }
        .aln-panel-divider-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(176,141,87,0.4), transparent);
        }
        .aln-panel-tagline {
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: rgba(176,141,87,0.5);
          line-height: 2;
        }

        /* Floating diamond ornaments */
        .aln-ornament {
          position: absolute;
          border: 1px solid rgba(176,141,87,0.15);
          transform: rotate(45deg);
        }
        .aln-ornament--1 { width: 140px; height: 140px; top: 18%; left: 12%; }
        .aln-ornament--2 { width: 80px; height: 80px; bottom: 22%; right: 15%; }
        .aln-ornament--3 { width: 220px; height: 220px; top: 55%; left: 50%; transform: rotate(45deg) translate(-50%,-50%); opacity: 0.4; }

        /* ── Right login panel ── */
        .aln-right {
          width: 100%;
          max-width: 520px;
          min-height: 100vh;
          background: #F4F2EE;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: clamp(40px, 8vw, 72px) clamp(28px, 6vw, 64px);
          position: relative;
          z-index: 2;
        }
        @media (min-width: 900px) {
          .aln-right { box-shadow: -2px 0 60px rgba(0,0,0,0.3); }
        }

        /* Top accent line */
        .aln-right::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #B08D57, #D4AF78, #B08D57);
        }

        .aln-logo-mobile {
          display: block;
          margin-bottom: 40px;
          text-align: center;
        }
        @media (min-width: 900px) {
          .aln-logo-mobile { display: none; }
        }
        .aln-logo-mobile-text {
          font-family: 'Playfair Display', serif;
          font-size: 28px;
          font-weight: 700;
          color: #1C1A18;
          letter-spacing: 0.04em;
        }
        .aln-logo-mobile-text span {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }

        /* Heading block */
        .aln-heading {
          margin-bottom: 36px;
        }
        .aln-eyebrow {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }
        .aln-eyebrow-text {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
        }
        .aln-eyebrow-line {
          flex: 1;
          max-width: 40px;
          height: 1px;
          background: #B08D57;
          opacity: 0.5;
        }
        .aln-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(26px, 4vw, 34px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.2;
        }
        .aln-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .aln-subtitle {
          font-size: 13px;
          font-weight: 300;
          color: #9A9186;
          margin-top: 8px;
          letter-spacing: 0.02em;
        }

        /* Fields */
        .aln-fields {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 28px;
        }
        .aln-field {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }
        .aln-label {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #6b6560;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .aln-input-wrap {
          position: relative;
        }
        .aln-input {
          width: 100%;
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: #1C1A18;
          background: #fff;
          border: 1px solid #e0d8cc;
          padding: 13px 44px 13px 16px;
          outline: none;
          transition: border-color 0.25s, box-shadow 0.25s;
          -webkit-appearance: none;
        }
        .aln-input::placeholder { color: #c0b8ae; font-weight: 300; }
        .aln-input:focus {
          border-color: #B08D57;
          box-shadow: 0 0 0 3px rgba(176,141,87,0.12);
        }
        .aln-input--focused { border-color: #B08D57; }
        .aln-input--error { border-color: #C0392B !important; box-shadow: 0 0 0 3px rgba(192,57,43,0.1) !important; }

        /* Input icon */
        .aln-input-icon {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #c0b8ae;
          transition: color 0.2s;
          display: flex;
          align-items: center;
          cursor: pointer;
          background: none;
          border: none;
          padding: 4px;
        }
        .aln-input-icon:hover { color: #B08D57; }
        .aln-input-icon svg { display: block; }

        /* Error message */
        .aln-error-wrap {
          background: #fdecea;
          border-left: 3px solid #C0392B;
          padding: 11px 14px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 20px;
        }
        .aln-error-text {
          font-size: 12px;
          color: #C0392B;
          font-weight: 400;
          line-height: 1.4;
        }

        /* Submit button */
        .aln-submit {
          width: 100%;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          background: #1C1A18;
          color: #fff;
          border: none;
          padding: 16px 32px;
          cursor: pointer;
          transition: background 0.25s, transform 0.1s;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        .aln-submit:hover:not(:disabled) { background: #B08D57; }
        .aln-submit:active:not(:disabled) { transform: scale(0.99); }
        .aln-submit:disabled { opacity: 0.7; cursor: not-allowed; }

        /* Loading spinner */
        .aln-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: aln-spin 0.7s linear infinite;
          flex-shrink: 0;
        }
        @keyframes aln-spin { to { transform: rotate(360deg); } }

        /* Shake keyframes */
        @keyframes aln-shake {
          0%, 100% { transform: translateX(0); }
          15%  { transform: translateX(-7px); }
          30%  { transform: translateX(7px); }
          45%  { transform: translateX(-5px); }
          60%  { transform: translateX(5px); }
          75%  { transform: translateX(-3px); }
          90%  { transform: translateX(3px); }
        }
        .aln-shake { animation: aln-shake 0.55s ease; }

        /* Footer note */
        .aln-footer {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid #e8e2d8;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .aln-footer-text {
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.08em;
          color: #b0a99e;
          line-height: 1.6;
        }

        /* Hint for demo */
        .aln-demo-hint {
          margin-top: 16px;
          font-size: 10px;
          color: #b0a99e;
          letter-spacing: 0.06em;
          text-align: center;
          padding: 10px 14px;
          border: 1px dashed #e0d8cc;
          background: #fdf9f4;
        }
        .aln-demo-hint strong {
          color: #B08D57;
          font-weight: 600;
          cursor: pointer;
        }
        .aln-demo-hint strong:hover { text-decoration: underline; }
      `}</style>

      <div className="aln-root">

        {/* ── Decorative left panel (desktop) ── */}
        <div className="aln-panel">
          <div className="aln-panel-bg" />
          <div className="aln-grid" />

          <div className="aln-corner aln-corner--tl" />
          <div className="aln-corner aln-corner--br" />

          {/* Diamond ornaments */}
          <div className="aln-ornament aln-ornament--1" />
          <div className="aln-ornament aln-ornament--2" />
          <div className="aln-ornament aln-ornament--3" />

          <motion.div
            className="aln-panel-content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="aln-panel-wordmark">
              Aamira
              <em>Jewellery</em>
            </div>

            <div className="aln-panel-divider">
              <div className="aln-panel-divider-line" />
              <Diamond size={5} />
              <div className="aln-panel-divider-line" />
            </div>

            <div className="aln-panel-tagline">
              Crafted with Tradition<br />
              Curated with Grace
            </div>
          </motion.div>
        </div>

        {/* ── Login form panel ── */}
        <motion.div
          className={`aln-right${shake ? ' aln-shake' : ''}`}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Mobile logo */}
          <div className="aln-logo-mobile">
            <div className="aln-logo-mobile-text">Aamira <span>Admin</span></div>
          </div>

          {/* Heading */}
          <div className="aln-heading">
            <div className="aln-eyebrow">
              <Diamond size={4} />
              <span className="aln-eyebrow-text">Admin Access</span>
              <div className="aln-eyebrow-line" />
            </div>
            <h1 className="aln-title">
              Welcome<br /><em>Back</em>
            </h1>
            <p className="aln-subtitle">Sign in to manage your jewellery store.</p>
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                className="aln-error-wrap"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="8" cy="8" r="7" stroke="#C0392B" strokeWidth="1.5" />
                  <path d="M8 4.5v4M8 11h.01" stroke="#C0392B" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span className="aln-error-text">{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="aln-fields">

              {/* Email */}
              <div className="aln-field">
                <label className="aln-label" htmlFor="aln-email">
                  <Diamond size={3} />
                  Email Address
                </label>
                <div className="aln-input-wrap">
                  <input
                    id="aln-email"
                    ref={emailRef}
                    className={`aln-input${error ? ' aln-input--error' : ''}`}
                    type="email"
                    autoComplete="email"
                    placeholder="admin@aamira.in"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    disabled={loading}
                    required
                  />
                  <span className="aln-input-icon" style={{ cursor: 'default', pointerEvents: 'none' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="M2 8l10 6 10-6" />
                    </svg>
                  </span>
                </div>
              </div>

              {/* Password */}
              <div className="aln-field">
                <label className="aln-label" htmlFor="aln-password">
                  <Diamond size={3} />
                  Password
                </label>
                <div className="aln-input-wrap">
                  <input
                    id="aln-password"
                    className={`aln-input${error ? ' aln-input--error' : ''}`}
                    type={showPass ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    className="aln-input-icon"
                    onClick={() => setShowPass(s => !s)}
                    tabIndex={-1}
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    {showPass ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="aln-submit"
              disabled={loading || !email || !password}
            >
              {loading ? (
                <>
                  <div className="aln-spinner" />
                  Authenticating...
                </>
              ) : (
                <>
                  <Diamond size={4} color="rgba(255,255,255,0.6)" />
                  Sign In to Dashboard
                </>
              )}
            </button>
          </form>

          {/* Demo hint — remove in production */}
          
        </motion.div>

      </div>
    </>
  );
};

export default AdminLogin;